import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest, apiBaseUrl } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { LastWorkPanel } from '../../components/LastWorkAtLocation.js';

interface PhotoVerdict { readonly photoId: string; readonly photoType: string; readonly verdict: 'PASS' | 'FAIL' | 'PENDING' }
interface ReviewDetail {
  readonly id: string;
  readonly jobRequestId: string;
  readonly jobCode: string;
  readonly status: string;
  readonly territoryId: string;
  readonly areaRequested: number;
  readonly areaUnit: string | null;
  readonly areaApproved: number | null;
  readonly photos: readonly { readonly id: string; readonly photoType: string }[];
  readonly photoVerdicts: readonly PhotoVerdict[];
  readonly gpsVerdict: string | null;
}

export function BoReviewPage(): ReactNode {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [review, setReview] = useState<ReviewDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [areaInput, setAreaInput] = useState('');
  const [remarks, setRemarks] = useState('');
  const [reason, setReason] = useState('');
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});

  /**
   * The image bytes, fetched rather than linked so the Authorization header
   * travels with the request. Without this the officer was asked to pass or
   * fail a photo that was never shown to him.
   */
  useEffect(() => {
    if (!review) return;
    let cancelled = false;
    const made: string[] = [];
    void (async () => {
      const next: Record<string, string> = {};
      for (const p of review.photos) {
        try {
          const res = await fetch(
            `${apiBaseUrl}/v1/job-requests/${review.jobRequestId}/photos/${p.id}/content`,
            { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include' },
          );
          if (!res.ok) continue;
          const url = URL.createObjectURL(await res.blob());
          made.push(url);
          next[p.id] = url;
        } catch { /* a photo that cannot be fetched simply stays unshown */ }
      }
      if (cancelled) { made.forEach((u) => { URL.revokeObjectURL(u); }); return; }
      setPhotoUrls(next);
    })();
    return () => {
      cancelled = true;
      made.forEach((u) => { URL.revokeObjectURL(u); });
    };
  }, [review, token]);

  async function load() {
    try {
      const r = await apiRequest<ReviewDetail>(`/v1/reviews/${id}`, { token });
      setReview(r);
      setAreaInput(String(r.areaApproved ?? r.areaRequested));
    } catch (e) { setError((e as Error).message); }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id, token]);

  async function photoVerdict(photoId: string, photoType: string, verdict: 'PASS' | 'FAIL') {
    setError(null); setInfo(null);
    // Backend PhotoVerdict enum is ACCEPTED/REJECTED; the UI speaks PASS/FAIL.
    const backendVerdict = verdict === 'PASS' ? 'ACCEPTED' : 'REJECTED';
    const remarks = verdict === 'FAIL' ? 'Photo rejected by branding officer' : '';
    try {
      await apiRequest(`/v1/reviews/${id}/photo-verdict`, {
        method: 'POST', token, body: { photoId, photoType, verdict: backendVerdict, remarks },
      });
      setInfo(`Photo ${photoType} ${verdict}`);
      await load();
    } catch (e) { setError((e as Error).message); }
  }

  async function gpsVerdict(verdict: 'PASS' | 'FAIL') {
    setError(null); setInfo(null);
    // Backend GpsVerdict enum is MATCH/MISMATCH; the UI speaks PASS/FAIL.
    const backendVerdict = verdict === 'PASS' ? 'MATCH' : 'MISMATCH';
    const remarks = verdict === 'FAIL' ? 'GPS location mismatch flagged by branding officer' : '';
    try {
      await apiRequest(`/v1/reviews/${id}/gps-verdict`, {
        method: 'POST', token, body: { verdict: backendVerdict, remarks },
      });
      setInfo(`GPS ${verdict}`);
      await load();
    } catch (e) { setError((e as Error).message); }
  }

  async function areaDecision() {
    const v = Number(areaInput);
    if (Number.isNaN(v) || v <= 0) { setError('Area must be > 0'); return; }
    setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/reviews/${id}/area-decision`, {
        method: 'POST', token, body: { areaApproved: v, remarks: '' },
      });
      setInfo('Area saved');
      await load();
    } catch (e) { setError((e as Error).message); }
  }

  async function approve() {
    if (!remarks) { setError('Remarks are required to approve'); return; }
    setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/reviews/${id}/approve`, {
        method: 'POST', token, body: { remarks, attachments: [] },
      });
      navigate(`/bo/assignment/${review?.jobRequestId ?? ''}`);
    } catch (e) { setError((e as Error).message); }
  }

  async function reject() {
    if (!reason) { setError('Reason is required to reject'); return; }
    if (!remarks) { setError('Remarks are required to reject'); return; }
    setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/reviews/${id}/reject`, {
        method: 'POST', token, body: { reason, remarks, attachments: [] },
      });
      navigate('/bo/queue');
    } catch (e) { setError((e as Error).message); }
  }

  async function returnForRework() {
    if (!reason) { setError('Reason is required to return for rework'); return; }
    if (!remarks) { setError('Remarks are required to return for rework'); return; }
    setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/reviews/${id}/return-for-rework`, {
        method: 'POST', token, body: { reason, remarks, attachments: [] },
      });
      navigate('/bo/queue');
    } catch (e) { setError((e as Error).message); }
  }

  async function photoClarify(photoId: string, photoType: string) {
    if (!remarks.trim()) { setError('Remarks are required to seek clarification'); return; }
    setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/reviews/${id}/photo-verdict`, {
        method: 'POST', token, body: { photoId, photoType, verdict: 'CLARIFICATION', remarks },
      });
      setInfo(`Clarification requested for ${photoType}`);
      await load();
    } catch (e) { setError((e as Error).message); }
  }

  async function pauseReview() {
    setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/reviews/${id}/pause`, { method: 'POST', token, body: {} });
      setInfo('Review paused');
      await load();
    } catch (e) { setError((e as Error).message); }
  }

  async function resumeReview() {
    setError(null); setInfo(null);
    try {
      await apiRequest(`/v1/reviews/${id}/resume`, { method: 'POST', token, body: {} });
      setInfo('Review resumed');
      await load();
    } catch (e) { setError((e as Error).message); }
  }

  if (error && !review) return <main aria-label="bo-review"><p role="alert">{error}</p></main>;
  if (!review) return <main aria-label="bo-review"><p>{t('bo.review.loading')}</p></main>;

  return (
    <main aria-label="bo-review">
      <h1>{t('bo.review.title', { code: review.jobCode })}</h1>
      <p aria-label="review-status">{t('bo.review.status')}: {review.status}</p>
      <section aria-label="review-controls">
        {review.status === 'PAUSED'
          ? <button onClick={resumeReview} aria-label="resume-review">{t('bo.review.resume')}</button>
          : <button onClick={pauseReview} aria-label="pause-review">{t('bo.review.pause')}</button>}
      </section>
      {error ? <p role="alert">{error}</p> : null}
      {info ? <p role="status">{info}</p> : null}

      {/* R14: the painting already done or in progress at this site and for
          this dealer — read before deciding, as at every approval level. */}
      <LastWorkPanel jobRequestId={review.jobRequestId} />

      <section aria-label="photo-section">
        <h2>{t('bo.review.photos')}</h2>
        {review.photos.length === 0 ? (
          <p aria-label="no-photos">{t('bo.review.noPhotos')}</p>
        ) : (
          <ul aria-label="photo-list">
            {review.photos.map((p) => {
              const v = review.photoVerdicts.find((x) => x.photoId === p.id);
              return (
                <li key={p.id} aria-label={`photo-${p.id}`}>
                  {photoUrls[p.id] ? (
                    <img
                      src={photoUrls[p.id]}
                      alt={p.photoType}
                      aria-label={`photo-image-${p.id}`}
                      style={{ display: 'block', maxWidth: '320px', height: 'auto' }}
                    />
                  ) : (
                    <p aria-label={`photo-unavailable-${p.id}`}>{t('bo.review.photoUnavailable')}</p>
                  )}
                  {p.photoType} — {t('bo.review.verdict')}: {v?.verdict ?? 'PENDING'}
                  <button onClick={() => photoVerdict(p.id, p.photoType, 'PASS')} aria-label={`pass-${p.id}`}>{t('bo.review.pass')}</button>
                  <button onClick={() => photoVerdict(p.id, p.photoType, 'FAIL')} aria-label={`fail-${p.id}`}>{t('bo.review.fail')}</button>
                  <button onClick={() => photoClarify(p.id, p.photoType)} aria-label={`clarify-${p.id}`}>{t('bo.review.clarify')}</button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section aria-label="gps-section">
        <h2>{t('bo.review.gps')}</h2>
        <p aria-label="gps-verdict">{review.gpsVerdict ?? 'PENDING'}</p>
        <button onClick={() => gpsVerdict('PASS')} aria-label="gps-pass">{t('bo.review.gpsPass')}</button>
        <button onClick={() => gpsVerdict('FAIL')} aria-label="gps-fail">{t('bo.review.gpsFail')}</button>
      </section>

      <section aria-label="area-section">
        <h2>{t('bo.review.area')}</h2>
        <label htmlFor="area-input">{t('bo.review.areaApproved', { unit: review.areaUnit ?? '—' })}</label>
        <input id="area-input" aria-label="area-approved" type="number" value={areaInput} onChange={(e) => setAreaInput(e.target.value)} />
        <button onClick={areaDecision} aria-label="save-area">{t('bo.review.saveArea')}</button>
      </section>

      <section aria-label="decision-section">
        <h2>{t('bo.review.decision')}</h2>
        <label htmlFor="remarks-input">{t('bo.review.remarks')}</label>
        <textarea id="remarks-input" aria-label="remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
        <label htmlFor="reason-input">{t('bo.review.rejectReason')}</label>
        <input id="reason-input" aria-label="reason" value={reason} onChange={(e) => setReason(e.target.value)} />
        <button onClick={approve} aria-label="approve-review">{t('bo.review.approve')}</button>
        <button onClick={reject} aria-label="reject-review">{t('bo.review.reject')}</button>
        <button onClick={returnForRework} aria-label="return-rework">{t('bo.review.returnRework')}</button>
      </section>
    </main>
  );
}
